import React from 'react';
import { Alert } from '@mui/material';
import { useAuth, usePermissions } from '../../hooks/useAuth';

const RoleGuard = ({ 
  children, 
  requiredPermission, 
  requiredPermissions, 
  requiredAllPermissions,
  requiredRole, 
  allowedRoles,
  requiredAction, // New prop for role-based action checking
  fallback,
  showError = true 
}) => {
  const { hasRole, hasAnyRole, canPerformAction } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  // Check role-based access
  if (requiredRole && !hasRole(requiredRole)) {
    return fallback || (showError ? (
      <Alert severity="error" sx={{ mt: 2 }}>
        Access denied. Required role: {requiredRole}
      </Alert>
    ) : null);
  }

  if (allowedRoles && !hasAnyRole(allowedRoles)) {
    return fallback || (showError ? (
      <Alert severity="error" sx={{ mt: 2 }}>
        Access denied. Your role is not authorized.
        <br />Allowed roles: {allowedRoles.join(', ')}
      </Alert>
    ) : null);
  }

  // Check action-based permissions (new role-based approach)
  if (requiredAction && !canPerformAction(requiredAction)) {
    return fallback || (showError ? (
      <Alert severity="error" sx={{ mt: 2 }}>
        Access denied. You don't have permission to perform this action.
      </Alert>
    ) : null);
  }

  // Backward compatibility - Check permission-based access
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return fallback || (showError ? (
      <Alert severity="warning" sx={{ mt: 2 }}>
        You don't have permission to view this content.
      </Alert>
    ) : null);
  }

  if (requiredPermissions && !hasAnyPermission(requiredPermissions)) {
    return fallback || (showError ? (
      <Alert severity="warning" sx={{ mt: 2 }}>
        You don't have any of the required permissions.
      </Alert>
    ) : null);
  }

  if (requiredAllPermissions && !hasAllPermissions(requiredAllPermissions)) {
    return fallback || (showError ? (
      <Alert severity="warning" sx={{ mt: 2 }}>
        You don't have all the required permissions.
      </Alert>
    ) : null);
  }

  return children;
};

export default RoleGuard;
